import { useEffect, useState } from "react";
import contentFallback from "../../data/content.json";
import { useCmsDoc } from "../../hooks/useCmsDoc";
import { Field, SaveBar, inputClass, textareaClass, saveCmsDoc } from "../cmsApi.jsx";
import IconPicker from "../components/IconPicker.jsx";
import CmsWithMediaSpecs from "../components/CmsMediaSpecsPanel.jsx";
import { MoveButtons, moveListItem } from "../components/MoveListControls.jsx";
import { listaHeroRefs, normalizarHeroRef, normalizeHeroMedios, filaVideoVacia, etiquetaProveedor, parseHeroClip, filasHeroVideos } from "../../lib/youtubeIds";

export default function CmsHomeEditor({ variant = "full", embedded = false }) {
  const { data, loading, source } = useCmsDoc("site", "home", {
    hero: contentFallback.hero,
    stats: contentFallback.stats,
    knowledgeHub: contentFallback.knowledgeHub,
    servicesSection: contentFallback.servicesSection,
  });
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!data) return;
    const next = structuredClone(data);
    if (!next.hero) next.hero = {};
    next.hero.medios = normalizeHeroMedios(next.hero.medios);
    const filas = filasHeroVideos(next.hero.medios);
    next.hero.medios.videos = filas.length ? filas : [filaVideoVacia()];
    setForm(next);
  }, [data]);

  if (loading || !form) {
    return <p className="text-sm text-brand-dark/50">Cargando Home…</p>;
  }

  const updateHero = (key, value) =>
    setForm((prev) => ({ ...prev, hero: { ...prev.hero, [key]: value } }));

  const updateMedios = (patch) =>
    setForm((prev) => ({
      ...prev,
      hero: {
        ...prev.hero,
        medios: { ...normalizeHeroMedios(prev.hero?.medios), ...patch },
      },
    }));

  const setVideoAt = (idx, patch) => {
    setForm((prev) => {
      const medios = normalizeHeroMedios(prev.hero?.medios);
      const list = [...(prev.hero?.medios?.videos?.length ? prev.hero.medios.videos : medios.videos)];
      while (list.length <= idx) list.push(filaVideoVacia());
      const nextRow = { ...filaVideoVacia(), ...list[idx], ...patch };
      if (patch.ref != null) {
        const clip = parseHeroClip(patch.ref);
        nextRow.proveedor = clip?.type || "";
        if (clip?.orientacion && patch.orientacion == null) {
          nextRow.orientacion = clip.orientacion;
        }
      }
      list[idx] = nextRow;
      return {
        ...prev,
        hero: { ...prev.hero, medios: { ...medios, videos: list, youtubeIds: list.map((r) => r.ref) } },
      };
    });
  };

  const addVideo = () => {
    setForm((prev) => {
      const current = prev.hero?.medios || {};
      const list = Array.isArray(current.videos) && current.videos.length
        ? [...current.videos]
        : filasHeroVideos(current);
      return {
        ...prev,
        hero: {
          ...prev.hero,
          medios: {
            ...current,
            videos: [...list, filaVideoVacia()],
          },
        },
      };
    });
  };

  const removeVideo = (idx) => {
    setForm((prev) => {
      const medios = normalizeHeroMedios(prev.hero?.medios);
      const list = [...(prev.hero?.medios?.videos || [])].filter((_, i) => i !== idx);
      const next = list.length ? list : [filaVideoVacia()];
      return {
        ...prev,
        hero: { ...prev.hero, medios: { ...medios, videos: next } },
      };
    });
  };

  const moveVideo = (idx, dir) => {
    setForm((prev) => {
      const medios = normalizeHeroMedios(prev.hero?.medios);
      const list = moveListItem(prev.hero?.medios?.videos || [], idx, dir);
      return { ...prev, hero: { ...prev.hero, medios: { ...medios, videos: list } } };
    });
  };

  const updateTopic = (idx, patch) => {
    setForm((prev) => {
      const topics = [...(prev.knowledgeHub?.topics || [])];
      topics[idx] = { ...topics[idx], ...patch };
      return { ...prev, knowledgeHub: { ...prev.knowledgeHub, topics } };
    });
  };

  const updateRecommendation = (topicIdx, recIdx, patch) => {
    setForm((prev) => {
      const topics = [...(prev.knowledgeHub?.topics || [])];
      const recommendations = [...(topics[topicIdx].recommendations || [])];
      recommendations[recIdx] = { ...recommendations[recIdx], ...patch };
      topics[topicIdx] = { ...topics[topicIdx], recommendations };
      return { ...prev, knowledgeHub: { ...prev.knowledgeHub, topics } };
    });
  };

  const addRecommendation = (topicIdx) => {
    setForm((prev) => {
      const topics = [...(prev.knowledgeHub?.topics || [])];
      topics[topicIdx] = {
        ...topics[topicIdx],
        recommendations: [
          ...(topics[topicIdx].recommendations || []),
          { icon: "sun", label: "Nuevo punto", desc: "" },
        ],
      };
      return { ...prev, knowledgeHub: { ...prev.knowledgeHub, topics } };
    });
  };

  const removeRecommendation = (topicIdx, recIdx) => {
    setForm((prev) => {
      const topics = [...(prev.knowledgeHub?.topics || [])];
      topics[topicIdx] = {
        ...topics[topicIdx],
        recommendations: (topics[topicIdx].recommendations || []).filter((_, i) => i !== recIdx),
      };
      return { ...prev, knowledgeHub: { ...prev.knowledgeHub, topics } };
    });
  };

  const addTopic = () => {
    setForm((prev) => ({
      ...prev,
      knowledgeHub: {
        ...prev.knowledgeHub,
        topics: [
          ...(prev.knowledgeHub?.topics || []),
          {
            id: `topic-${Date.now()}`,
            title: "Nuevo protocolo",
            subtitle: "",
            description: "",
            videoUrl: "",
            event: "",
            recommendations: [
              { icon: "sun", label: "Punto 1", desc: "" },
            ],
          },
        ],
      },
    }));
  };

  const removeTopic = (idx) => {
    setForm((prev) => ({
      ...prev,
      knowledgeHub: {
        ...prev.knowledgeHub,
        topics: (prev.knowledgeHub?.topics || []).filter((_, i) => i !== idx),
      },
    }));
  };

  const moveTopic = (idx, dir) => {
    setForm((prev) => ({
      ...prev,
      knowledgeHub: {
        ...prev.knowledgeHub,
        topics: moveListItem(prev.knowledgeHub?.topics || [], idx, dir),
      },
    }));
  };

  const moveRecommendation = (topicIdx, recIdx, dir) => {
    setForm((prev) => {
      const topics = [...(prev.knowledgeHub?.topics || [])];
      topics[topicIdx] = {
        ...topics[topicIdx],
        recommendations: moveListItem(topics[topicIdx].recommendations || [], recIdx, dir),
      };
      return { ...prev, knowledgeHub: { ...prev.knowledgeHub, topics } };
    });
  };

  const serviceCards = form.servicesSection?.items || [];

  const updateServiceCard = (idx, patch) => {
    setForm((prev) => {
      const items = [...(prev.servicesSection?.items || [])];
      items[idx] = { ...items[idx], ...patch };
      return { ...prev, servicesSection: { ...prev.servicesSection, items } };
    });
  };

  const addServiceCard = () => {
    setForm((prev) => ({
      ...prev,
      servicesSection: {
        ...prev.servicesSection,
        items: [
          ...(prev.servicesSection?.items || []),
          {
            id: `svc-${Date.now()}`,
            title: "Nuevo servicio",
            description: "",
            icon: "shield",
            link: "/servicios",
            cta: "Explorar Servicio",
          },
        ],
      },
    }));
  };

  const removeServiceCard = (idx) => {
    setForm((prev) => ({
      ...prev,
      servicesSection: {
        ...prev.servicesSection,
        items: (prev.servicesSection?.items || []).filter((_, i) => i !== idx),
      },
    }));
  };

  const moveServiceCard = (idx, dir) => {
    setForm((prev) => ({
      ...prev,
      servicesSection: {
        ...prev.servicesSection,
        items: moveListItem(prev.servicesSection?.items || [], idx, dir),
      },
    }));
  };

  const onSave = async () => {
    setSaving(true);
    setError("");
    setMessage("");
    try {
      const { id, updatedAt, updatedBy, ...payload } = form;
      if (payload.hero) {
        payload.hero = {
          ...payload.hero,
          medios: (() => {
            const n = normalizeHeroMedios(payload.hero.medios);
            const filas = filasHeroVideos({ videos: payload.hero.medios?.videos, youtubeIds: payload.hero.medios?.youtubeIds });
            return {
              videos: filas.filter((f) => parseHeroClip(f.ref)),
              youtubeIds: listaHeroRefs(filas.map((f) => f.ref)),
              mensajeRail: n.mensajeRail,
              youtubePrivacy: n.youtubePrivacy,
              cropTopPx: n.cropTopPx,
              cropBottomPx: n.cropBottomPx,
              autoplayMuted: n.autoplayMuted,
              enHero: n.enHero,
              tituloReproductor: n.tituloReproductor,
              reproductorAncho: n.reproductorAncho,
              reproductorAlto: n.reproductorAlto,
              marcoColor: n.marcoColor,
              marcoAncho: n.marcoAncho,
              desbordar: n.desbordar,
            };
          })(),
        };
      }
      await saveCmsDoc("site", "home", payload);
      setMessage(
        variant === "inteligencia"
          ? "Centro de inteligencia guardado."
          : variant === "servicios"
            ? "Tarjetas de servicio guardadas."
            : variant === "medios"
            ? "Medios guardados."
            : variant === "hero"
              ? "Hero guardado."
              : "Home guardado correctamente."
      );
    } catch (err) {
      setError(err?.message || "Error al guardar.");
    } finally {
      setSaving(false);
    }
  };

  const showHero = variant === "full" || variant === "hero";
  const showMedios = variant === "full" || variant === "medios";
  const showInteligencia = variant === "full" || variant === "inteligencia";
  const showServiceCards = variant === "full" || variant === "servicios";

  return (
    <div className="space-y-8 max-w-5xl">
      {!embedded ? (
        <div>
          <h2 className="font-display text-3xl font-black">Home</h2>
          <p className="text-sm text-brand-dark/55 mt-1">Fuente: {source}</p>
        </div>
      ) : (
        <p className="text-sm text-brand-dark/55">Fuente: {source}</p>
      )}

      {showHero ? (
      <section className="bg-white rounded-[28px] border border-brand-soft p-6 space-y-4">
        <h3 className="font-bold text-lg">Hero</h3>
        <Field label="Badge">
          <input className={inputClass} value={form.hero?.badge || ""} onChange={(e) => updateHero("badge", e.target.value)} />
        </Field>
        <Field label="Título">
          <input className={inputClass} value={form.hero?.title || ""} onChange={(e) => updateHero("title", e.target.value)} />
        </Field>
        <Field label="Título destacado">
          <input className={inputClass} value={form.hero?.titleHighlight || ""} onChange={(e) => updateHero("titleHighlight", e.target.value)} />
        </Field>
        <Field label="Subtítulo">
          <textarea className={textareaClass} value={form.hero?.subtitle || ""} onChange={(e) => updateHero("subtitle", e.target.value)} />
        </Field>
        <CmsWithMediaSpecs variant="images">
          <Field label="URL video fondo (MP4, si no hay YouTube)">
            <input className={inputClass} value={form.hero?.videoUrl || ""} onChange={(e) => updateHero("videoUrl", e.target.value)} />
          </Field>
          <Field label="URL poster">
            <input className={inputClass} value={form.hero?.posterUrl || ""} onChange={(e) => updateHero("posterUrl", e.target.value)} />
          </Field>
        </CmsWithMediaSpecs>
      </section>
      ) : null}

      {showMedios ? (
      <section className="bg-white rounded-[28px] border border-brand-soft p-6 space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="font-bold text-lg">Medios</h3>
            <p className="text-sm text-brand-dark/55 mt-1">
              Playlist del reproductor junto al Centro de inteligencia. Acepta video, Short
              y listas de YouTube (playlist?list=), o Facebook. Opcionalmente puede volver a
              usarse como fondo del HERO.
            </p>
          </div>
          <button
            type="button"
            onClick={addVideo}
            className="shrink-0 rounded-xl border border-brand-primary/40 bg-brand-soft px-4 py-2 text-xs font-bold uppercase tracking-widest text-brand-primary hover:bg-brand-primary hover:text-white"
          >
            + Agregar video
          </button>
        </div>
        <label className="flex items-start gap-3 text-sm text-brand-dark/80 border border-brand-soft rounded-2xl px-4 py-3">
          <input
            type="checkbox"
            className="mt-1"
            checked={Boolean(form.hero?.medios?.enHero)}
            onChange={(e) => updateMedios({ enHero: e.target.checked })}
          />
          <span>
            <strong>Reproducir también en el HERO</strong>
            <span className="block text-brand-dark/50 mt-0.5">
              Apagado: el HERO usa solo el video MP4/poster. Encendido: la misma playlist llena el
              fondo. El audio es exclusivo: si suena el HERO se silencia el teléfono, y al revés.
            </span>
          </span>
        </label>

        <div className="rounded-[24px] border border-brand-soft bg-[#f8faf9] p-5 space-y-4">
          <h4 className="font-bold text-base">Reproductor</h4>
          <p className="text-xs text-brand-dark/50">
            Columna derecha del home, junto al Centro de inteligencia. En escritorio puede
            desbordarse sobre la sección siguiente (p. ej. Servicios). En móvil se ajusta al
            ancho de pantalla, sin desborde.
          </p>
          <Field label="Nombre del reproductor (saltos de línea permitidos)">
            <textarea
              className={textareaClass}
              rows={3}
              placeholder={"Nuestro contenido\nde YouTube"}
              value={form.hero?.medios?.tituloReproductor || ""}
              onChange={(e) => updateMedios({ tituloReproductor: e.target.value })}
            />
          </Field>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Ancho (px)">
              <input
                type="number"
                min={160}
                max={640}
                className={inputClass}
                value={form.hero?.medios?.reproductorAncho ?? 280}
                onChange={(e) => updateMedios({ reproductorAncho: Number(e.target.value) || 0 })}
              />
            </Field>
            <Field label="Alto (px)">
              <input
                type="number"
                min={240}
                max={960}
                className={inputClass}
                value={form.hero?.medios?.reproductorAlto ?? 640}
                onChange={(e) => updateMedios({ reproductorAlto: Number(e.target.value) || 0 })}
              />
            </Field>
            <Field label="Color del marco">
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  className="h-10 w-12 shrink-0 rounded-lg border border-brand-soft bg-white p-1"
                  value={
                    /^#[0-9a-fA-F]{6}$/.test(form.hero?.medios?.marcoColor || "")
                      ? form.hero.medios.marcoColor
                      : "#ffffff"
                  }
                  onChange={(e) => updateMedios({ marcoColor: e.target.value })}
                />
                <input
                  className={inputClass}
                  value={form.hero?.medios?.marcoColor || "#ffffff"}
                  onChange={(e) => updateMedios({ marcoColor: e.target.value })}
                  placeholder="#ffffff"
                />
              </div>
            </Field>
            <Field label="Ancho del marco (px)">
              <input
                type="number"
                min={0}
                max={48}
                className={inputClass}
                value={form.hero?.medios?.marcoAncho ?? 10}
                onChange={(e) => updateMedios({ marcoAncho: Number(e.target.value) || 0 })}
              />
            </Field>
          </div>
          <label className="flex items-start gap-3 text-sm text-brand-dark/80">
            <input
              type="checkbox"
              className="mt-1"
              checked={form.hero?.medios?.desbordar !== false}
              onChange={(e) => updateMedios({ desbordar: e.target.checked })}
            />
            <span>
              Permitir desborde en escritorio
              <span className="block text-brand-dark/50 mt-0.5">
                El teléfono puede extenderse sobre el cambio de color hacia Inteligencia preventiva
                / Servicios. En móvil nunca desborda.
              </span>
            </span>
          </label>
        </div>

        <Field label="Mensaje del riel inferior (solo HERO)">
          <input
            className={inputClass}
            placeholder="Grupo AGDYSA · Preservando la vida"
            value={form.hero?.medios?.mensajeRail || ""}
            onChange={(e) => updateMedios({ mensajeRail: e.target.value })}
          />
        </Field>
        <div className="grid sm:grid-cols-3 gap-3">
          <Field label="Privacidad">
            <select
              className={inputClass}
              value={form.hero?.medios?.youtubePrivacy || "unlisted"}
              onChange={(e) => updateMedios({ youtubePrivacy: e.target.value })}
            >
              <option value="unlisted">No listado</option>
              <option value="public">Público</option>
              <option value="private">Privado (no embebe)</option>
            </select>
          </Field>
          <Field label="Recorte superior chrome YT (px)">
            <input
              type="number"
              min={0}
              max={120}
              className={inputClass}
              value={form.hero?.medios?.cropTopPx ?? 55}
              onChange={(e) => updateMedios({ cropTopPx: Number(e.target.value) || 0 })}
            />
          </Field>
          <Field label="Recorte inferior chrome YT (px)">
            <input
              type="number"
              min={0}
              max={120}
              className={inputClass}
              value={form.hero?.medios?.cropBottomPx ?? 58}
              onChange={(e) => updateMedios({ cropBottomPx: Number(e.target.value) || 0 })}
            />
          </Field>
        </div>
        <p className="text-xs text-brand-dark/55">
          Acepta video normal, Short (/shorts/…) y lista de YouTube (playlist?list=…, por ejemplo
          VIDEOS AGDYSA). Facebook: enlace o iframe. El reproductor detecta vertical u horizontal;
          puede corregirlo en cada fila. Duración (s) opcional (0 = YouTube avanza al terminar;
          una lista recorre internamente; Facebook 60 s).
        </p>
        <ul className="space-y-2">
        {(form.hero?.medios?.videos || [filaVideoVacia()]).map((row, idx) => {
          const etiqueta = etiquetaProveedor(row.ref) || (row.proveedor === "facebook" ? "Facebook" : row.proveedor === "youtube" ? "YouTube" : "");
          return (
          <li key={`vid-${idx}`} className="grid gap-2 rounded-2xl border border-brand-soft p-3 sm:grid-cols-12 sm:items-end">
            <div className="sm:col-span-2">
              <span className="text-[11px] font-bold uppercase tracking-widest text-brand-dark/40">Origen</span>
              <p
                className={`mt-1 rounded-xl px-3 py-2 text-xs font-bold ${
                  etiqueta.startsWith("YouTube")
                    ? "bg-red-50 text-red-700"
                    : etiqueta === "Facebook"
                      ? "bg-blue-50 text-blue-800"
                      : "bg-[#f8faf9] text-brand-dark/45"
                }`}
              >
                {etiqueta || "Pegue el enlace"}
              </p>
            </div>
            <div className="min-w-0 sm:col-span-5">
              <Field label={idx === 0 ? "Enlace 1" : `Enlace ${idx + 1}`}>
                <textarea
                  className={textareaClass}
                  rows={2}
                  placeholder="https://www.youtube.com/shorts/…  ·  playlist?list=…  ·  Facebook"
                  value={row.ref || ""}
                  onChange={(e) => setVideoAt(idx, { ref: e.target.value })}
                  onBlur={(e) => {
                    const ref = normalizarHeroRef(e.target.value);
                    if (ref) setVideoAt(idx, { ref });
                  }}
                />
              </Field>
            </div>
            <div className="sm:col-span-2">
              <Field label="Formato">
                <select
                  className={inputClass}
                  value={row.orientacion === "horizontal" || row.orientacion === "vertical" ? row.orientacion : (parseHeroClip(row.ref)?.orientacion || "horizontal")}
                  onChange={(e) => setVideoAt(idx, { orientacion: e.target.value })}
                >
                  <option value="vertical">Vertical</option>
                  <option value="horizontal">Horizontal</option>
                </select>
              </Field>
            </div>
            <div className="sm:col-span-1">
              <Field label="Seg.">
                <input
                  type="number"
                  min={0}
                  max={3600}
                  className={inputClass}
                  value={row.duracionSeg || 0}
                  onChange={(e) => setVideoAt(idx, { duracionSeg: Number(e.target.value) || 0 })}
                />
              </Field>
            </div>
            <div className="flex items-center gap-2 sm:col-span-2 sm:justify-end">
              <MoveButtons
                idx={idx}
                total={(form.hero?.medios?.videos || []).length}
                onMove={(dir) => moveVideo(idx, dir)}
              />
              <button
                type="button"
                onClick={() => removeVideo(idx)}
                className="text-xs font-bold text-red-600"
              >
                Quitar
              </button>
            </div>
          </li>
          );
        })}
        </ul>
        <button
          type="button"
          onClick={addVideo}
          className="text-sm font-semibold text-brand-primary underline underline-offset-2 hover:text-brand-dark"
        >
          + Agregar video
        </button>
        <p className="text-[11px] text-brand-dark/45">
          Cuantas filas necesite. YouTube: video, Short o lista (playlist?list=). Facebook: reel o iframe.
        </p>
      </section>
      ) : null}

      {showInteligencia ? (
      <section className="bg-white rounded-[28px] border border-brand-soft p-6 space-y-4">
        <div className="flex items-center justify-between gap-3">
          <h3 className="font-bold text-lg">Protocolos / Centro de inteligencia</h3>
          <button type="button" onClick={addTopic} className="text-xs font-bold uppercase tracking-widest text-brand-primary">
            + Tema
          </button>
        </div>
        <CmsWithMediaSpecs variant="icons">
        <Field label="Badge sección">
          <input
            className={inputClass}
            value={form.knowledgeHub?.badge || ""}
            onChange={(e) =>
              setForm((prev) => ({
                ...prev,
                knowledgeHub: { ...prev.knowledgeHub, badge: e.target.value },
              }))
            }
          />
        </Field>
        <Field label="Título sección">
          <input
            className={inputClass}
            value={form.knowledgeHub?.title || ""}
            onChange={(e) =>
              setForm((prev) => ({
                ...prev,
                knowledgeHub: { ...prev.knowledgeHub, title: e.target.value },
              }))
            }
          />
        </Field>
        <Field label="Título destacado (opcional, línea en cursiva)">
          <input
            className={inputClass}
            placeholder="y Seguridad Ocupacional"
            value={form.knowledgeHub?.titleHighlight || ""}
            onChange={(e) =>
              setForm((prev) => ({
                ...prev,
                knowledgeHub: { ...prev.knowledgeHub, titleHighlight: e.target.value },
              }))
            }
          />
        </Field>

        {(form.knowledgeHub?.topics || []).map((topic, idx) => (
          <div key={topic.id || idx} className="border border-brand-soft rounded-2xl p-4 space-y-4">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <p className="text-xs font-bold uppercase tracking-widest text-brand-dark/40">Tema {idx + 1}</p>
                <MoveButtons
                  idx={idx}
                  total={(form.knowledgeHub?.topics || []).length}
                  onMove={(dir) => moveTopic(idx, dir)}
                />
              </div>
              <button type="button" onClick={() => removeTopic(idx)} className="text-xs text-red-600 font-bold">
                Quitar tema
              </button>
            </div>

            <Field label="Título">
              <input className={inputClass} value={topic.title || ""} onChange={(e) => updateTopic(idx, { title: e.target.value })} />
            </Field>
            <Field label="Subtítulo">
              <input className={inputClass} value={topic.subtitle || ""} onChange={(e) => updateTopic(idx, { subtitle: e.target.value })} />
            </Field>
            <Field label="Descripción (visible si no hay video)">
              <textarea className={textareaClass} value={topic.description || ""} onChange={(e) => updateTopic(idx, { description: e.target.value })} />
            </Field>
            <Field label="URL video YouTube (embed). Si se llena, el tema muestra video + puntos a la izquierda">
              <input
                className={inputClass}
                value={topic.videoUrl || ""}
                placeholder="https://www.youtube.com/embed/..."
                onChange={(e) => updateTopic(idx, { videoUrl: e.target.value })}
              />
            </Field>
            <Field label="Evento / pie (opcional)">
              <input
                className={inputClass}
                value={topic.event || ""}
                placeholder="Conferencia en Madrid, 13 de mayo de 2026"
                onChange={(e) => updateTopic(idx, { event: e.target.value })}
              />
            </Field>

            <div className="rounded-2xl bg-[#f8faf9] border border-brand-soft p-4 space-y-3">
              <div className="flex items-center justify-between gap-2">
                <div>
                  <p className="text-sm font-bold text-brand-dark">Tarjetas / puntos del tema</p>
                  <p className="text-[11px] text-brand-dark/50 mt-0.5">
                    Sin video: grid grande a la derecha. Con video: lista en el panel verde.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => addRecommendation(idx)}
                  className="text-xs font-bold uppercase tracking-widest text-brand-primary shrink-0"
                >
                  + Punto
                </button>
              </div>

              {(topic.recommendations || []).map((rec, rIdx) => (
                <div key={rIdx} className="bg-white border border-brand-soft rounded-xl p-3 space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <p className="text-[10px] font-bold uppercase tracking-widest text-brand-dark/40">
                        Punto {rIdx + 1}
                      </p>
                      <MoveButtons
                        idx={rIdx}
                        total={(topic.recommendations || []).length}
                        onMove={(dir) => moveRecommendation(idx, rIdx, dir)}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => removeRecommendation(idx, rIdx)}
                      className="text-xs text-red-600 font-bold"
                    >
                      Quitar
                    </button>
                  </div>
                  <IconPicker
                    label="Icono"
                    value={rec.icon || "proteccion-solar"}
                    onChange={(icon) => updateRecommendation(idx, rIdx, { icon })}
                  />
                  <input
                    className={inputClass}
                    value={rec.label || ""}
                    placeholder="Etiqueta (ej. Hidratación)"
                    onChange={(e) => updateRecommendation(idx, rIdx, { label: e.target.value })}
                  />
                  <textarea
                    className={textareaClass}
                    value={rec.desc || ""}
                    placeholder="Descripción corta"
                    onChange={(e) => updateRecommendation(idx, rIdx, { desc: e.target.value })}
                  />
                </div>
              ))}
            </div>
          </div>
        ))}
        </CmsWithMediaSpecs>
      </section>
      ) : null}

      {showServiceCards ? (
      <section className="bg-white rounded-[28px] border border-brand-soft p-6 space-y-4">
        <div className="flex items-center justify-between gap-3">
          <h3 className="font-bold text-lg">Tarjetas de servicios (Home)</h3>
          <button type="button" onClick={addServiceCard} className="text-xs font-bold uppercase tracking-widest text-brand-primary">
            + Tarjeta
          </button>
        </div>
        <CmsWithMediaSpecs variant="icons">
        {serviceCards.map((card, idx) => (
          <div key={card.id || idx} className="border border-brand-soft rounded-2xl p-4 space-y-3">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <p className="text-xs font-bold uppercase tracking-widest text-brand-dark/40">Tarjeta {idx + 1}</p>
                <MoveButtons
                  idx={idx}
                  total={serviceCards.length}
                  onMove={(dir) => moveServiceCard(idx, dir)}
                />
              </div>
              <button type="button" onClick={() => removeServiceCard(idx)} className="text-xs text-red-600 font-bold">
                Quitar
              </button>
            </div>
            <input className={inputClass} value={card.title || ""} onChange={(e) => updateServiceCard(idx, { title: e.target.value })} placeholder="Título" />
            <textarea
              className={textareaClass}
              value={card.description || card.desc || ""}
              onChange={(e) => updateServiceCard(idx, { description: e.target.value, desc: e.target.value })}
              placeholder="Descripción"
            />
            <div className="grid sm:grid-cols-2 gap-2">
              <IconPicker
                label="Icono"
                value={card.icon || "mediciones-higienicas"}
                onChange={(icon) => updateServiceCard(idx, { icon })}
              />
              <Field label="CTA">
                <input
                  className={inputClass}
                  value={card.cta || ""}
                  placeholder="CTA"
                  onChange={(e) => updateServiceCard(idx, { cta: e.target.value })}
                />
              </Field>
            </div>
            <Field label="Link">
              <input
                className={inputClass}
                value={card.link || ""}
                placeholder="Link /servicios?id=..."
                onChange={(e) => updateServiceCard(idx, { link: e.target.value })}
              />
            </Field>
          </div>
        ))}
        </CmsWithMediaSpecs>
      </section>
      ) : null}

      <SaveBar saving={saving} onSave={onSave} message={message} error={error} />
    </div>
  );
}
